import { Autocomplete, Box, Card, Checkbox, FormControl, IconButton, InputLabel, Link, MenuItem, Select, SelectChangeEvent, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TextField, useTheme } from "@mui/material"
import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import PageHeader from "../../../components/PageHeader"
import { useAppSelector } from "../../../redux/store"
import { InactiveLinearProgress, PrimaryLinearProgress, SuccessLinearProgress, WarningLinearProgress } from "../../../components/LinearProgress.styles"
import { selectCustomer, selectChildPractices, selectGroups, selectShowAutoclaveConfirmationAlertForCorp, selectLoadingState } from "../../../redux/customer"
import { useSelector } from "react-redux"
import { Groups, LoadingState } from "../../../types"
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DataGrid } from "@mui/x-data-grid"
import { usePromiseTracker } from "react-promise-tracker"
import { selectIsAdminOrOwner } from "../../../redux/user"
import LoadingScreen from "../../../components/LoadingScreen"
import constants from "../../../constants"

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function ConfirmAutoclaveType() {
  const navigate = useNavigate()
  const { promiseInProgress } = usePromiseTracker()
  const theme = useTheme()
  const groups = useSelector(selectGroups)
  const childPractices = useAppSelector(selectChildPractices);
  const customer = useAppSelector(selectCustomer)
  const isAdminOrOwner = useAppSelector(selectIsAdminOrOwner)
  const showAutoclaveConfirmation = useAppSelector(selectShowAutoclaveConfirmationAlertForCorp)
  const loadingState = useAppSelector(selectLoadingState)

  const isDataLoaded = Boolean(customer.customer_number)
    && loadingState === LoadingState.Done

  useEffect(() => {
    if (!isDataLoaded || !isAdminOrOwner) return
    if (!showAutoclaveConfirmation) {
      navigate('/', { replace: true })
    }
  }, [isDataLoaded, isAdminOrOwner, showAutoclaveConfirmation, navigate])

  type AutoclaveStatusProperty =
    | 'autoclave_total'
    | 'autoclave_outstanding'
    | 'autoclave_confirmed_count'
    | 'autoclave_deactivated_count'

  const [complianceFilter, setComplianceFilter] = useState<AutoclaveStatusProperty>('autoclave_total');
  const [groupFilter, setGroupFilter] = useState<Groups[]>([]);
  const [search, setSearch] = useState<string>('');

  const handleComplianceFilter = (event: SelectChangeEvent<any>) => {
    setComplianceFilter(event.target.value as AutoclaveStatusProperty)
  };

  const handleCheckboxComplianceFilter = (value: AutoclaveStatusProperty) => {
    setComplianceFilter(value)
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearch(value);
  };

  interface ComplianceCounts {
    totalAutoclaves: number;
    totalAutoclavesPercentage: number;
    outstandingConfirmations: number;
    outstandingConfirmationsPercentage: number;
    confirmed: number;
    confirmedPercentage: number;
    deactivated: number;
    deactivatedPercentage: number;
  }

  type Props = {
    complianceFilter: AutoclaveStatusProperty;
    complianceCounts: ComplianceCounts;
  };

  const filteredPractices = useMemo(() => {
    let practices = structuredClone(childPractices) ?? [];

    if (search.length > 0) {
      const cleanSearch = search.toLowerCase();
      practices = practices.filter(({ customer_number, name, street, city, state }) => {
        return customer_number.includes(cleanSearch)
          || name.toLowerCase().includes(cleanSearch)
          || street.toLowerCase().includes(cleanSearch)
          || city.toLowerCase().includes(cleanSearch)
          || state.toLowerCase().includes(cleanSearch)
      })
    }

    // Status filter: keep only practices whose selected status is > 0.
    practices = practices.filter((practice) => Number((practice as any)?.[complianceFilter] ?? 0) > 0)

    if (groupFilter.length) {
      const members = new Set(
        groupFilter.flatMap((group) => group?.members?.map((member) => member.customer_number) ?? [])
      )
      if (members.size > 0) {
        practices = practices.filter((customer) => members.has(customer.customer_number))
      }
    }

    return practices;
  }, [childPractices, complianceFilter, groupFilter, search])

  const complianceCounts = useMemo<ComplianceCounts>(() => {
    const practices = structuredClone(childPractices) ?? []
    const getStatus = (practice: unknown, key: "autoclave_total" | "autoclave_outstanding" | "autoclave_confirmed_count" | "autoclave_deactivated_count") => {
      const value = (practice as Record<string, unknown>)[key]
      return typeof value === "number" ? value : Number(value) || 0
    }

    const totalAutoclaves = practices.reduce((sum, practice) => sum + getStatus(practice, "autoclave_total"), 0)
    const outstandingConfirmations = practices.reduce((sum, practice) => sum + getStatus(practice, "autoclave_outstanding"), 0)
    const confirmed = practices.reduce((sum, practice) => sum + getStatus(practice, "autoclave_confirmed_count"), 0)
    const deactivated = practices.reduce((sum, practice) => sum + getStatus(practice, "autoclave_deactivated_count"), 0)

    const getPercentage = (count: number) => {
      if (totalAutoclaves <= 0) return 0
      return Math.round((count / totalAutoclaves) * 100)
    }

    return {
      totalAutoclaves,
      totalAutoclavesPercentage: 100,
      outstandingConfirmations,
      outstandingConfirmationsPercentage: getPercentage(outstandingConfirmations),
      confirmed,
      confirmedPercentage: getPercentage(confirmed),
      deactivated,
      deactivatedPercentage: getPercentage(deactivated),
    }
  }, [childPractices])

  const ConfirmationStatus = ({ complianceFilter, complianceCounts }: Props) => {
    return (
      <Card variant="outlined">
        <div className="box-header">Confirmation Status</div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%' }}>Practices</TableCell>
              <TableCell sx={{ width: '30%' }}></TableCell>
              <TableCell sx={{ width: '40%' }}>Status</TableCell>
              <TableCell sx={{ width: '20%' }}>Filter by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {!promiseInProgress && (
                <>
                  <TableCell>
                    <span className="compliance-box background-primary">{complianceCounts.totalAutoclaves}</span>
                  </TableCell>
                  <TableCell>
                    <PrimaryLinearProgress variant="determinate" value={complianceCounts.totalAutoclavesPercentage} />
                  </TableCell>
                </>
              )}
              {promiseInProgress && (
                <>
                  <TableCell>
                    <Skeleton variant="rounded" width={24} height={23} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" height={8}></Skeleton>
                  </TableCell>
                </>
              )}
              <TableCell>Total Autoclaves</TableCell>
              <TableCell>
                <Checkbox
                  checked={complianceFilter === 'autoclave_total'}
                  onChange={() => handleCheckboxComplianceFilter('autoclave_total')}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              {!promiseInProgress && (
                <>
                  <TableCell>
                    <span className="compliance-box background-warning">{complianceCounts.outstandingConfirmations}</span>
                  </TableCell>
                  <TableCell>
                    <WarningLinearProgress variant="determinate" value={complianceCounts.outstandingConfirmationsPercentage} />
                  </TableCell>
                </>
              )}
              {promiseInProgress && (
                <>
                  <TableCell>
                    <Skeleton variant="rounded" width={24} height={23} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" height={8}></Skeleton>
                  </TableCell>
                </>
              )}
              <TableCell>Outstanding Confirmations</TableCell>
              <TableCell>
                <Checkbox
                  checked={complianceFilter === 'autoclave_outstanding'}
                  onChange={() => handleCheckboxComplianceFilter('autoclave_outstanding')}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              {!promiseInProgress && (
                <>
                  <TableCell>
                    <span className="compliance-box background-success">{complianceCounts.confirmed}</span>
                  </TableCell>
                  <TableCell>
                    <SuccessLinearProgress variant="determinate" value={complianceCounts.confirmedPercentage} />
                  </TableCell>
                </>
              )}
              {promiseInProgress && (
                <>
                  <TableCell>
                    <Skeleton variant="rounded" width={24} height={23} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" height={8}></Skeleton>
                  </TableCell>
                </>
              )}
              <TableCell>Confirmed</TableCell>
              <TableCell>
                <Checkbox
                  checked={complianceFilter === 'autoclave_confirmed_count'}
                  onChange={() => handleCheckboxComplianceFilter('autoclave_confirmed_count')}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              {!promiseInProgress && (
                <>
                  <TableCell>
                    <span className="compliance-box background-inactive">{complianceCounts.deactivated}</span>
                  </TableCell>
                  <TableCell>
                    <InactiveLinearProgress variant="determinate" value={complianceCounts.deactivatedPercentage} />
                  </TableCell>
                </>
              )}
              {promiseInProgress && (
                <>
                  <TableCell>
                    <Skeleton variant="rounded" width={24} height={23} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" height={8}></Skeleton>
                  </TableCell>
                </>
              )}
              <TableCell>Deactivated</TableCell>
              <TableCell>
                <Checkbox
                  checked={complianceFilter === 'autoclave_deactivated_count'}
                  onChange={() => handleCheckboxComplianceFilter('autoclave_deactivated_count')}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
    );
  };

  const filtersContent = (
    <div className="flex flex-space-between m-16">
      <div className="flex">
        <FormControl sx={{ mr: 2, minWidth: 200 }} size="small">
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={complianceFilter}
            label="Filter by Compliance"
            onChange={handleComplianceFilter}
          >
            <MenuItem value="autoclave_total">Total Autoclaves</MenuItem>
            <MenuItem value="autoclave_outstanding">Outstanding Confirmations</MenuItem>
            <MenuItem value="autoclave_confirmed_count">Confirmed</MenuItem>
            <MenuItem value="autoclave_deactivated_count">Deactivated</MenuItem>
          </Select>
        </FormControl>
        {groups.length > 0 && (
          <Autocomplete
            multiple
            value={groupFilter}
            onChange={(event, newValue) => {
              setGroupFilter(newValue);
            }}
            options={groups}
            isOptionEqualToValue={(option, value) => option.group_id === value.group_id}
            disableCloseOnSelect
            getOptionLabel={(option) => option.name}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                {option.name}
              </li>
            )}
            size="small"
            style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
            renderInput={(params) => <TextField {...params} label="Filter by Group" />}
          />
        )}
      </div>
      <TextField
        size="small"
        variant="outlined"
        placeholder="Search"
        value={search}
        onChange={handleSearch}
        InputProps={{
          endAdornment: (
            <IconButton>
              <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
            </IconButton>
          )
        }}
      />
    </div>
  )

  const AutoclaveTable = () => {
    return (
      <DataGrid
        sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
        rows={filteredPractices.map((practice) => ({ ...practice, id: practice.customer_number }))}
        columns={[
          {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 500,
            renderCell: (params) => (
              <Link component="button" disabled={params.row?.autoclave_outstanding === 0} onClick={() => navigate(`/corp/practice/${params.row.customer_number}/biological/confirm-autoclave-type`)} sx={{ color: params.row?.autoclave_outstanding === 0 ? theme.palette.text.disabled : theme.palette.linkColor.main, textDecoration: 'none' }}>
                {params.row.name.trim()} ({params.row.customer_number})
              </Link>
            )
          },
          {
            field: "city",
            headerName: "Location",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
              `${params.row.city}, ${params.row.state}`
            )
          },
          { field: "autoclave_total", headerName: "Total Autoclaves", width: 125 },
          { field: "autoclave_outstanding", headerName: "Outstanding Confirmations", width: 200 },
          { field: "autoclave_confirmed_count", headerName: "Confirmed", width: 125 },
          { field: "autoclave_deactivated_count", headerName: "Deactivated", width: 125 },
        ]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
          sorting: {
            sortModel: [
              {
                field: 'name',
                sort: 'asc'
              }
            ]
          }
        }}
        pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        loading={promiseInProgress}
        autoHeight
        density="compact"
      />
    )
  }

  const RecallLink = () => {
    return (
      <Link
        sx={{ ml: 1, fontSize: "16px", cursor: "pointer" }}
        href={constants.MESA_RECALL_2026_PDF_URL}
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        Why are we asked to do this?
      </Link>
    )
  }

  return (
    (!isDataLoaded) ? (
      <LoadingScreen />
    ) : (
      <Box>
        <PageHeader iconName="square-check" headerName={<>Autoclave Confirmation Status <RecallLink /></>} size="26px" />
        <Box sx={{ m: 1.5 }}>
          <ConfirmationStatus
            complianceFilter={complianceFilter}
            complianceCounts={complianceCounts}
          />
        </Box>
        <Card variant="outlined" sx={{ m: 1.5 }}>
          <div className="box-header b-b">Practices</div>
          {filtersContent}
          <AutoclaveTable />
        </Card>
      </Box>
    )
  )
}

